import type { Taken } from "@aybuben/client/device/polaroid-memory.ts";
import { STAGGER } from "@aybuben/client/motion.ts";
import type { MomentTiming } from "@aybuben/client/places/moment.ts";
import type { Stop } from "@aybuben/client/places/stops.ts";
import { resultModel, TREE_CUE_MS, treeHeadline } from "@aybuben/client/screens/result.ts";
import type { CueKind } from "@aybuben/client/sound/kit.ts";
import type { ProgressKey } from "@aybuben/core/content/types.ts";
import type { LangId } from "@aybuben/core/lang.ts";
import type { LessonSummary } from "@aybuben/core/progress/reduce.ts";
import type { TreeMoment } from "@aybuben/core/progress/tree.ts";
import { Crown } from "lucide-react";
import { Suspense, useEffect, useRef, useState } from "react";

import { play } from "../audio.ts";
import { useCourse } from "../content/context.tsx";
import { haptic } from "../haptics.ts";
import { useStrings } from "../i18n/index.tsx";
import { COL } from "../theme.ts";
import { lazyPart, PartBoundary, SkipPart, usePartError } from "./lazy.tsx";
import { Btn } from "./primitives.tsx";
import { Tree } from "./tree/index.tsx";
import { Moments, StageUp } from "./tree/moment.tsx";
import { Scene } from "./tree/Scene.tsx";

/** The result's entrance, one beat per block, in reading order. */
const beat = (i: number) => ({ animationDelay: `${i * STAGGER}ms` });

// Lazy: the moment draws the world art, which the startup bundle cannot carry.
export const polaroidMoment = lazyPart(() =>
	import("./home/PolaroidMoment.tsx").then((m) => m.PolaroidMoment),
);
const PolaroidMoment = polaroidMoment.Part;

interface ResultProps {
	res: LessonSummary;
	/** What the lesson did to the tree: the screen grows it and says so. */
	moment: TreeMoment;
	/** The learner's tree shape (`tree/seeds.ts`). */
	seed?: number;
	/** The Polaroids' date format. */
	lang: LangId;
	/** The stops this lesson finished, in course order: each one's moment plays before anything else. */
	polaroids?: { stop: Stop; taken: Taken | null }[];
	cue?: (kind: CueKind) => void;
	momentTiming?: MomentTiming;
	onPolaroidShutter?: (id: string) => void;
	onPolaroidShare?: (id: string, el: HTMLElement) => void;
	/** A moment ended: its photo flew home. */
	onPinned?: () => void;
	/** False when the tree did not change: only the Polaroid moments play, then `onContinue`. */
	treeStep?: boolean;
	/** Once, when the tree step opens (after any moments). */
	onShown?: () => void;
	/** A new stage's full-screen celebration is up; dismissing it calls `onCelebrated`. */
	celebrating?: boolean;
	onCelebrated?: () => void;
	onContinue: () => void;
	onReviewKeys: (keys: ProgressKey[]) => void;
	/** A round toward summer, offered while the tree is short of it. */
	onWater?: () => void;
}

export function Result({
	res,
	moment,
	seed,
	lang,
	polaroids,
	cue = play,
	momentTiming,
	onPolaroidShutter,
	onPolaroidShare,
	onPinned,
	treeStep = true,
	onShown,
	celebrating = false,
	onCelebrated,
	onContinue,
	onReviewKeys,
	onWater,
}: ResultProps) {
	const course = useCourse();
	const t = useStrings();
	const m = resultModel(res, moment);
	const onPartError = usePartError("polaroid-moment");
	const [queue, setQueue] = useState(polaroids ?? []);
	const cuesPlayed = useRef(false);
	const idle = queue.length === 0;
	// Once, when the moments are over: the result's chime must not sound under a shutter.
	useEffect(() => {
		if (!idle || cuesPlayed.current) return;
		cuesPlayed.current = true;
		// A skipped tree step plays nothing and hands straight home.
		if (!treeStep) {
			onContinue();
			return;
		}
		onShown?.();
		haptic("reveal");
		const c = m.treeCue;
		if (!c) return;
		const id = setTimeout(() => cue(c), TREE_CUE_MS);
		return () => clearTimeout(id);
	}, [idle]);
	const current = queue[0];
	const nextMoment = () => {
		setQueue((q) => q.slice(1));
		onPinned?.();
	};
	return (
		<>
			{/* Behind a moment, the result is out of reach: focus and the screen reader stay on the photo. */}
			{treeStep && (
				<div
					data-testid="result"
					inert={current !== undefined}
					className="flex min-h-screen flex-col items-center px-6 pt-14 text-center"
					style={{ paddingBottom: 140 }}
				>
					<div className="motion-enter flex w-full justify-center" style={beat(0)}>
						<Scene
							season={moment.to.season}
							stage={moment.to.stage}
							className="h-52 w-full max-w-xs rounded-[2.5rem]"
						>
							<Tree tree={moment.to} from={moment.from} variant="full" seed={seed} />
						</Scene>
					</div>
					<h1
						className="motion-pop mt-4 text-3xl font-extrabold"
						style={{ color: COL.pom, ...beat(1) }}
					>
						{t(treeHeadline(moment, m.blurb))}
					</h1>
					{m.unlocked.map((id: string, i: number) => (
						<div
							key={id}
							className="motion-enter mt-3 flex w-full items-center gap-3 rounded-3xl p-4 text-left"
							style={{ background: COL.lapisSoft, color: COL.lapisDark, ...beat(2 + i) }}
						>
							<Crown size={22} />
							<span className="font-semibold">
								{t("result.unlocked", { n: course.ordinal(id), title: course.t(`${id}.title`) })}
							</span>
						</div>
					))}
					<Moments moment={moment} onWater={onWater} />
					<div className="fixed inset-x-0 bottom-0 z-20" style={{ background: COL.bg }}>
						<div
							className="mx-auto max-w-md px-5 pt-4"
							style={{ paddingBottom: "max(20px, env(safe-area-inset-bottom))" }}
						>
							<Btn onClick={onContinue} color={COL.pom}>
								{t("result.continue")}
							</Btn>
						</div>
					</div>
				</div>
			)}
			{current && (
				// Keyed per stop: a failed chunk skips only its own moment, and the next one tries again.
				<PartBoundary
					key={current.stop.id}
					// A failed moment is skipped: the photo is already pinned in the memory.
					fallback={<SkipPart onSkip={nextMoment} />}
					onError={onPartError}
				>
					<Suspense fallback={null}>
						<PolaroidMoment
							stop={current.stop}
							taken={current.taken}
							lang={lang}
							cue={cue}
							timing={momentTiming}
							onShutter={() => onPolaroidShutter?.(current.stop.id)}
							onShare={(el) => onPolaroidShare?.(current.stop.id, el)}
							onDone={nextMoment}
						/>
					</Suspense>
				</PartBoundary>
			)}
			{celebrating && idle && onCelebrated && (
				<StageUp moment={moment} seed={seed} onDone={onCelebrated} />
			)}
		</>
	);
}
